/**
 * CoilNpcAppearances — hand-authored per-NPC portrait configs.
 *
 * Started as "the Hollow Coil cast" (Coil Art Pack, Lane B 2026-07-01: tomas,
 * merchant_maria, elder_wynn, maren, sophia, old_ferris, ranger_cole) and
 * grew (2026-07-07) to also cover the 8 NPCs of the orphan `starter_zone` —
 * grant, guide_rafael, guard_captain, villager_bob, blacksmith_erik,
 * arcanist_luna, hunter_kael, shadow_dealer. Those 8 are NOT Hollow Coil
 * characters — they live in a separate, unreachable-in-normal-play zone (see
 * content/npcs/starter_zone.toml, and FORWARD_QUEUE.md's orphan-zone note).
 * The map/file keep the `COIL_*` name for historical continuity and minimal
 * churn (only this file + its test import the symbol — see the 2026-07-07
 * starter-zone-portraits task report for why (a) "extend the existing map"
 * was chosen over (b) "new sibling file"): read `COIL_NPC_APPEARANCES` as
 * "the hand-authored NPC portrait registry," not a promise every entry is
 * Coil canon.
 *
 * Keyed by NPC id (the content/npcs/*.toml table key, which is also the
 * server schema key — see EntitySpawnerService.spawnZoneNPCs). Unlike the
 * role presets in AppearancePresets.ts (one look per `npc.type`), these are
 * individual characters.
 *
 * The hand-authored Hollow Coil portrait subset carries the island's
 * cozy→spooky gradient (colors
 * pulled from docs/design/coil-art/NPC_CHARACTER_SHEETS.md /
 * COLOR_PALETTE.md — do not invent colors for THIS cast):
 *   tomas (village_arrival)          — warm, sun-weathered, straw hat
 *   merchant_maria (village_arrival) — bright, market-busy, apron + potion bottles
 *   elder_wynn (tidewend)      — dusk-purple robe, silver hair, cane
 *   maren (tidewend)           — amber tunic, leather apron, auburn bun
 *   sophia (tidewend)          — dark-green explorer cloak, map satchel
 *   old_ferris (tidewend)      — faded-indigo sweater, full salt-grey beard, belt lantern
 *   ranger_cole (brackenmoor)  — cold teal-grey hood, bow, belt lantern
 *   field_warden (first_field)    — Warden Lark: field hat + feather, moss mantle, survey spear w/ pennant
 *   rim_keeper (moonfall_hollow)  — Maelis of the Rim: wind-white hair, streaming teal scarf, crystal staff
 * Naturalist Nessa and the Tidewend trainers/banker still use their role
 * presets — plan 12 (docs/plans/2026-10-07-fun-and-polish/12-NPC-ART-UPGRADE.md)
 * rolls the art profile out to them once DJ approves the samples.
 *
 * The entries live in npcAppearances/ (one file per cast); this module is the
 * facade every consumer imports.
 *
 * The starter_zone 8 have no biome/gradient to key off (the zone is orphaned,
 * not part of the live cozy→spooky map chain — see
 * docs/plans/2026-07-07-dormant-bestiary-art.md's Global Constraints) — each
 * is designed instead from role + dialogue characterization in
 * content/npcs/starter_zone.toml:
 *   grant (trainer)            — grizzled veteran, scarred, oxblood battle-leathers
 *   guard_captain (guard)      — stern young officer, steel-blue uniform + navy cloak
 *   villager_bob (civilian)    — soft, rosy-cheeked, big grin, patched homespun
 *   guide_rafael (guide)       — energetic trailblazer, wink, satchel, trail green
 *   blacksmith_erik (shop)     — brawny smith, forge-flushed skin, iron-grey apron
 *   arcanist_luna (shop)       — pale mystic, star eyes, glowing violet robe
 *   hunter_kael (shop)         — rugged tracker, olive woodsman green, bow
 *   shadow_dealer (shop)       — hooded black-market dealer, near-black cloak
 *
 * Silhouette identifiers (readable at game scale before any dialogue):
 * Coil — hat · apron+bottles · cane · apron+bun · cloak · full-beard+lantern · hood+bow.
 * Starter-zone — scar · cloak+uniform · blush+grin · wink+satchel ·
 * smith-apron · star-eyes+glow · bow+stubble · hood+near-black-cloak.
 *
 * All 15 characters render unarmed (`weapon` always undefined via the
 * `npc()` helper below) — even the combat-adjacent roles (grant/trainer,
 * guard_captain/guard, hunter_kael/shop-ranger) — on the reasoning that this
 * is a portrait layer, not a combat loadout: WeaponParts rendering exists for
 * entities that fight, and none of these 15 ever spawn as a fighting unit.
 * Where class signature matters (Kael sells bows, so he plausibly carries
 * one), it's expressed via the `props` layer instead — "held/worn detail",
 * not a drawn combat weapon.
 */

import type { HumanoidAppearance } from './GhostRenderer';
import { appearanceForNpcRole } from './AppearancePresets';
import { HOLLOW_COIL_CAST } from './npcAppearances/hollowCoilCast';
import { STARTER_ZONE_CAST } from './npcAppearances/starterZoneCast';
import { TIDEWEND_TRAINERS } from './npcAppearances/tidewendTrainers';
import { WICKHAVEN_CAST } from './npcAppearances/wickhavenCast';
import { PORT_CAPTAINS } from './npcAppearances/portCaptains';
import { BRANCH_CAST } from './npcAppearances/branchCast';
import { FOREST_JOURNEY_CAST } from './npcAppearances/forestJourneyCast';
import { HUSH_NOOK_CAST } from './npcAppearances/hushNookCast';

export const COIL_NPC_APPEARANCES: Record<string, HumanoidAppearance> = {
  ...HOLLOW_COIL_CAST,
  ...TIDEWEND_TRAINERS,
  ...STARTER_ZONE_CAST,
  ...WICKHAVEN_CAST,
  ...PORT_CAPTAINS,
  ...BRANCH_CAST,
  ...FOREST_JOURNEY_CAST,
  ...HUSH_NOOK_CAST,
};

/** True when this NPC id has a hand-authored Coil portrait. */
export function hasCustomNpcAppearance(npcId?: string): boolean {
  return !!npcId && Object.prototype.hasOwnProperty.call(COIL_NPC_APPEARANCES, npcId);
}

/**
 * Per-NPC appearance lookup: custom Coil portrait by NPC id, falling back to
 * the role-based preset for everyone else (keeps non-Coil NPCs working).
 */
export function appearanceForNpc(npcId: string | undefined, npcType: string | undefined): HumanoidAppearance {
  if (npcId && hasCustomNpcAppearance(npcId)) return COIL_NPC_APPEARANCES[npcId];
  return appearanceForNpcRole(npcType);
}
